/* Fiori contact and welcome UI. Checkout eligibility is configured in Shopify. */
const session = {
  get(key) { try { return sessionStorage.getItem(key); } catch { return null; } },
  set(key, value = '1') { try { sessionStorage.setItem(key, value); } catch { /* Storage can be unavailable. */ } },
};
const modalOpen = () => [...document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"]:not([hidden])')].some(el => {
  const style = getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden' && el.getClientRects().length > 0;
});
// Horizon can scroll a page wrapper instead of the window.
const pageScroll = () => {
  const wrapper = document.querySelector('.page-wrapper');
  const scroller = wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1 &&
    /auto|scroll/.test(getComputedStyle(wrapper).overflowY) ? wrapper : document.scrollingElement;
  const distance = scroller ? scroller.scrollHeight - scroller.clientHeight : 0;
  const top = scroller ? scroller.scrollTop : scrollY;
  return { top, progress: distance > 0 ? top / distance * 100 : 0 };
};

class FioriWhatsapp extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const opts = { signal: this.controller.signal };
    this.launcher = this.querySelector('[data-wa-toggle]');
    this.panel = this.querySelector('[data-wa-panel]');
    this.key = 'fiori-wa-introduced';
    this.introduced = !!session.get(this.key);
    this.launcher.addEventListener('click', () => {
      this.markIntroduced();
      if (this.panel.hidden || !this.pinned) { this.open(false); this.pinned = true; }
      else this.close();
    }, opts);
    this.querySelector('[data-wa-close]').addEventListener('click', () => {
      this.markIntroduced(); this.close(); this.launcher.focus();
    }, opts);
    this.addEventListener('pointerenter', event => {
      if (event.pointerType !== 'mouse' || !matchMedia('(hover: hover)').matches) return;
      clearTimeout(this.closeTimer); clearTimeout(this.autoTimer); this.open(false);
    }, opts);
    this.addEventListener('pointerleave', event => {
      if (event.pointerType === 'mouse') this.closeTimer = setTimeout(() => {
        if (!this.pinned && !this.querySelector(':focus-visible')) this.close();
      }, 350);
    }, opts);
    this.addEventListener('focusin', () => clearTimeout(this.autoTimer), opts);
    this.addEventListener('focusout', () => {
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        if (!this.pinned && !this.matches(':hover') && !this.contains(document.activeElement)) this.close();
      }, 350);
    }, opts);
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape' && !this.panel.hidden) { this.close(); this.launcher.focus(); }
    }, opts);
    document.addEventListener('pointerdown', event => {
      if (!this.contains(event.target)) this.close();
    }, opts);
    this.tick = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
    };
    document.addEventListener('scroll', this.tick, { ...opts, passive: true, capture: true });
    window.addEventListener('resize', this.tick, opts);
    document.addEventListener('fiori:offer', this.tick, opts);
    this.observer = new MutationObserver(this.tick);
    this.observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open', 'data-sticky', 'data-visible', 'aria-hidden'] });
    this.interval = setInterval(this.tick, 1000);
    this.update();
  }
  markIntroduced() { this.introduced = true; session.set(this.key); }
  open(automatic) {
    if (modalOpen()) return;
    this.panel.hidden = false; this.launcher.setAttribute('aria-expanded', 'true');
    clearTimeout(this.autoTimer);
    if (automatic) this.autoTimer = setTimeout(() => {
      if (!this.matches(':hover') && !this.contains(document.activeElement)) this.close();
    }, Number(this.dataset.duration) * 1000);
  }
  close() {
    clearTimeout(this.autoTimer); this.pinned = false; this.panel.hidden = true; this.launcher.setAttribute('aria-expanded', 'false');
  }
  update() {
    const blocked = modalOpen();
    this.hidden = blocked;
    if (blocked) this.close();
    let offset = 0;
    for (const el of document.querySelectorAll('.sticky-add-to-cart__bar')) {
      const r = el.getBoundingClientRect();
      if (r.height && r.top < innerHeight && r.bottom > innerHeight - 10 && getComputedStyle(el).visibility !== 'hidden') offset = Math.max(offset, innerHeight - r.top);
    }
    document.documentElement.style.setProperty('--fiori-sticky-offset', `${offset}px`);
    const { top, progress } = pageScroll();
    if (!blocked && !this.introduced && this.dataset.auto === 'true' && top > 0 && progress >= Number(this.dataset.scroll)) {
      this.markIntroduced(); this.open(true);
    }
  }
  disconnectedCallback() {
    this.controller?.abort(); this.controller = null; this.observer?.disconnect();
    clearTimeout(this.autoTimer); clearTimeout(this.closeTimer); clearInterval(this.interval); cancelAnimationFrame(this.frame);
  }
}

class FioriWelcome extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController(); const opts = { signal: this.controller.signal };
    this.dialog = this.querySelector('dialog'); this.teaser = this.querySelector('[data-offer-open]');
    this.key = `fiori-offer-${this.dataset.campaign}`;
    this.teaser.addEventListener('click', () => this.open(), opts);
    this.querySelector('[data-offer-close]').addEventListener('click', () => this.dialog.close(), opts);
    this.dialog.addEventListener('click', event => { if (event.target === this.dialog) {
      const r = this.dialog.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close();
    } }, opts);
    this.dialog.addEventListener('close', () => {
      session.set(this.key); this.classList.remove('is-open');
      document.dispatchEvent(new Event('fiori:offer'));
      this.teaser.focus({ preventScroll: true });
    }, opts);
    this.querySelector('[data-offer-login]')?.addEventListener('click', () => session.set('fiori-offer-return', this.dataset.campaign), opts);
    this.querySelector('[data-offer-copy]')?.addEventListener('click', async event => {
      const code = this.querySelector('[data-offer-code]').textContent.trim();
      try {
        await navigator.clipboard.writeText(code);
        this.querySelector('[data-offer-status]').textContent = this.dataset.copied;
      } catch {
        const range = document.createRange(); range.selectNodeContents(this.querySelector('[data-offer-code]'));
        const selection = getSelection(); selection.removeAllRanges(); selection.addRange(range);
        this.querySelector('[data-offer-status]').textContent = this.dataset.select;
      }
    }, opts);
    const returning = this.dataset.eligible === 'true' && session.get('fiori-offer-return') === this.dataset.campaign;
    if (returning) session.set('fiori-offer-return', '');
    if (this.dataset.preview === 'true' || returning || !session.get(this.key)) {
      this.timer = setTimeout(() => this.tryAutomatic(), returning ? 700 : Number(this.dataset.delay) * 1000);
    }
    document.addEventListener('visibilitychange', () => { if (!document.hidden && this.pending) this.tryAutomatic(); }, opts);
  }
  tryAutomatic() {
    if (!this.isConnected) return;
    if (document.hidden || modalOpen()) { this.pending = true; this.timer = setTimeout(() => this.tryAutomatic(), 1500); return; }
    this.pending = false; this.open();
  }
  open() {
    if (this.dialog.open || modalOpen()) return;
    clearTimeout(this.timer); session.set(this.key);
    this.classList.add('is-open'); this.dialog.showModal();
    document.dispatchEvent(new Event('fiori:offer'));
  }
  disconnectedCallback() { clearTimeout(this.timer); this.controller?.abort(); this.controller = null; if (this.dialog?.open) this.dialog.close(); }
}
if (!customElements.get('fiori-whatsapp')) customElements.define('fiori-whatsapp', FioriWhatsapp);
if (!customElements.get('fiori-welcome')) customElements.define('fiori-welcome', FioriWelcome);
